import type { JsonObject } from '@/data/types/json';
import type { UserEntity } from '@/domain/entities/user/userEntity';
import type { UserStorage } from '@/domain/entities/secureStorage/secureStorageEntities';
import type { UserDto } from '../../../data/dtos/user/userDto';

/**
 * UserRemapper 
 */
export class UserRemapper {
  /**
   * Maps raw Firestore data to User DTO.
   * Handles field name differences and provides default values.   
   */
  static fromJson(data: JsonObject): UserDto {
    return {
      id: (data['id'] as string | undefined) || '',
      firebaseId: (data['firebase_id'] as string | undefined) || (data['firebaseId'] as string | undefined) || '',
      firstName: (data['first_name'] as string | undefined) || (data['firstName'] as string | undefined) || '',
      lastName: (data['last_name'] as string | undefined) || (data['lastName'] as string | undefined) || '',
      username: (data['username'] as string | undefined) || '',
      name: (data['name'] as string | undefined) || '',
      bio: data['bio'] as string | undefined,
      profileImage: (data['profile_image'] as string | undefined) ?? (data['profileImage'] as string | undefined),
      city: data['city'] as string | undefined,
      country: data['country'] as string | undefined,
      createdTimestamp: (data['created_timestamp'] as number | undefined) || (data['createdTimestamp'] as number | undefined) || 0,
      lastSeenTimestamp: (data['last_seen_timestamp'] as number | undefined) ?? (data['lastSeenTimestamp'] as number | undefined),
      email: data['email'] as string | undefined,
      popularity: (data['popularity'] as number | undefined) || 0,
      contentCount: (data['contentCount'] as number | undefined) || 0,
      birthdate: data['birthdate'] as number | undefined,
      pushToken: data['pushToken'] as string | undefined,
      onboarded: (data['onboarded'] as boolean | undefined) || false,
      subscriptionCharge: (data['subscription_charge'] as number | undefined) || (data['subscriptionCharge'] as number | undefined) || 0,
      shareLink: (data['shareLink'] as string | undefined) || '',
      blocked: (data['blocked'] as boolean | undefined) || false,
      suspended: (data['suspended'] as boolean | undefined) || false,
      removing: (data['removing'] as boolean | undefined) || false,
      removed: (data['removed'] as boolean | undefined) || false,
      subscribers: (data['subscribers'] as number | undefined) || 0,
      likes: (data['likes'] as number | undefined) || 0,
      reports: (data['reports'] as number | undefined) || 0,
      contentReports: (data['contentReports'] as number | undefined) || 0,
      blockedUsers: data['blockedUsers'] as string[] | undefined,
      usersBlockedMe: data['usersBlockedMe'] as string[] | undefined,
      mutedUsers: data['mutedUsers'] as string[] | undefined,
      blockedConversations: data['blockedConversations'] as string[] | undefined,
      originalTransactionIds: (data['original_transaction_ids'] as string[] | undefined) ?? (data['originalTransactionIds'] as string[] | undefined),
      type: data['type'] as number | undefined,
      publicJwk: data['publicJwk'] as Record<string, any> | undefined,
      prefferedHashTags: data['prefferedHashTags'] as string[] | undefined,
      prefferedContentTags: data['prefferedContentTags'] as string[] | undefined,
      platform: data['platform'] as number | undefined,
      hiddenContent: data['hiddenContent'] as string[] | undefined,
      firstLastNameJoinedLowerCase: (data['firstLastNameLower'] as string | undefined) || (data['firstLastNameJoinedLowerCase'] as string | undefined) || '',
      lastFirstNameJoinedLowerCase: (data['lastFirstNameLower'] as string | undefined) || (data['lastFirstNameJoinedLowerCase'] as string | undefined) || '',
    };
  }

  /**
   * Map raw Firestore data to User Entity
   */
  static toEntity(data: any): UserEntity {
    const dto = this.fromJson(data);
    return { ...dto } as UserEntity;
  }

  /**
   * Map an array of raw user objects to User models
   */
  static fromJsonList(dataList: JsonObject[]): UserDto[] {
    return dataList.map(data => this.fromJson(data));
  }

  /**
   * Convert UserEntity to UserStorage for encrypted storage
   * Used by storeCurrentUserToLocalStorageUseCase
   */
  static toStorage(entity: UserEntity): UserStorage {
    return {
      id: entity.id,
      firebaseId: entity.firebaseId,
      firstName: entity.firstName,
      lastName: entity.lastName,
      username: entity.username,
      name: entity.name,
      bio: entity.bio,
      profileImage: entity.profileImage,
      city: entity.city,
      country: entity.country,
      createdTimestamp: entity.createdTimestamp,
      lastSeenTimestamp: entity.lastSeenTimestamp,
      email: entity.email,
      popularity: entity.popularity,
      contentCount: entity.contentCount,
      birthdate: entity.birthdate,
      pushToken: entity.pushToken,
      onboarded: entity.onboarded,
      subscriptionCharge: entity.subscriptionCharge,
      shareLink: entity.shareLink,
      blocked: entity.blocked,
      suspended: entity.suspended,
      removing: entity.removing,
      removed: entity.removed,
      subscribers: entity.subscribers,
      likes: entity.likes,
      reports: entity.reports,
      contentReports: entity.contentReports,
      blockedUsers: entity.blockedUsers,
      usersBlockedMe: entity.usersBlockedMe,
      mutedUsers: entity.mutedUsers,
      blockedConversations: entity.blockedConversations,
      originalTransactionIds: entity.originalTransactionIds,
      type: entity.type,
      publicJwk: entity.publicJwk,
      prefferedHashTags: entity.prefferedHashTags,
      prefferedContentTags: entity.prefferedContentTags,
      platform: entity.platform,
      hiddenContent: entity.hiddenContent,
      firstLastNameJoinedLowerCase: entity.firstLastNameJoinedLowerCase,
      lastFirstNameJoinedLowerCase: entity.lastFirstNameJoinedLowerCase,
    };
  }

  /**
   * Convert UserStorage back to UserEntity
   * Used by fetchCurrentUserFromLocalStorageUseCase
   */
  static fromStorage(storage: UserStorage): UserEntity {
    return {
      id: storage.id,
      firebaseId: storage.firebaseId,
      firstName: storage.firstName,
      lastName: storage.lastName,
      username: storage.username,
      name: storage.name,
      bio: storage.bio,
      profileImage: storage.profileImage,
      city: storage.city,
      country: storage.country,
      createdTimestamp: storage.createdTimestamp,
      lastSeenTimestamp: storage.lastSeenTimestamp,
      email: storage.email,
      popularity: storage.popularity,
      contentCount: storage.contentCount,
      birthdate: storage.birthdate,
      pushToken: storage.pushToken,
      onboarded: storage.onboarded,
      subscriptionCharge: storage.subscriptionCharge,
      shareLink: storage.shareLink,
      blocked: storage.blocked,
      suspended: storage.suspended,
      removing: storage.removing,
      removed: storage.removed,
      subscribers: storage.subscribers,
      likes: storage.likes,
      reports: storage.reports,
      contentReports: storage.contentReports,
      blockedUsers: storage.blockedUsers,
      usersBlockedMe: storage.usersBlockedMe,
      mutedUsers: storage.mutedUsers,
      blockedConversations: storage.blockedConversations,
      originalTransactionIds: storage.originalTransactionIds,
      type: storage.type,
      publicJwk: storage.publicJwk,
      prefferedHashTags: storage.prefferedHashTags,
      prefferedContentTags: storage.prefferedContentTags,
      platform: storage.platform,
      hiddenContent: storage.hiddenContent,
      firstLastNameJoinedLowerCase: storage.firstLastNameJoinedLowerCase,
      lastFirstNameJoinedLowerCase: storage.lastFirstNameJoinedLowerCase,
    };
  }
}
